import type { HeadlessSignals } from "../types";
import { safe } from "../utils/safe";

/**
 * 7-signal headless heuristic, ported from the formv3 teardown.
 * Each signal is a known automation/emulation tell; `score` ≥ 4 ⇒ likely bot.
 *
 * @param unmaskedRenderer WebGL unmasked renderer string (reused, not re-probed).
 */
export function collectHeadless(unmaskedRenderer: string | null): HeadlessSignals {
  const webdriver = safe(() => (navigator as Navigator).webdriver === true, false);

  const pluginsZero = safe(() => (navigator.plugins?.length ?? 0) === 0, false);

  const languagesZero = safe(() => (navigator.languages?.length ?? 0) === 0, false);

  const swiftShader = safe(
    () => /swiftshader|llvmpipe|software/i.test(unmaskedRenderer ?? ""),
    false
  );

  // Chrome present in UA but window.chrome.runtime missing ⇒ stripped automation build.
  const chromeNoRuntime = safe(() => {
    const w = window as Window & { chrome?: { runtime?: unknown } };
    const looksChrome = /chrome|crios/i.test(navigator.userAgent);
    return looksChrome && !!w.chrome && !w.chrome.runtime;
  }, false);

  const screenZero = safe(() => screen.width === 0 || screen.height === 0, false);

  const outerZero = safe(() => window.outerWidth === 0 || window.outerHeight === 0, false);

  const flags = [
    webdriver,
    pluginsZero,
    languagesZero,
    swiftShader,
    chromeNoRuntime,
    screenZero,
    outerZero,
  ];
  const score = flags.reduce((acc, f) => acc + (f ? 1 : 0), 0);

  return {
    webdriver,
    pluginsZero,
    languagesZero,
    swiftShader,
    chromeNoRuntime,
    screenZero,
    outerZero,
    score,
    isHeadless: score >= 4,
  };
}
